/**
 * Reads reviews rendered by Judge.me's hidden app-embed carousel widget and re-builds them into
 * this theme's own card design inside [data-vc-reviews-track], then runs a small dependency-free
 * auto-scrolling carousel (native scroll-snap, no Flickity/CDN). Works for any review count.
 */

function truncate(str, maxLength) {
  if (!str) return '';
  const trimmed = str.trim();
  if (trimmed.length <= maxLength) return { text: trimmed, truncated: false };
  return { text: trimmed.slice(0, maxLength - 1).trimEnd() + '…', truncated: true };
}

function starsMarkup(label) {
  const match = label.match(/(\d+(\.\d+)?)/);
  const rating = match ? Math.min(5, Math.max(0, Math.round(parseFloat(match[1])))) : 5;
  const star = (fill) =>
    `<svg width="14" height="14" viewBox="0 0 14 14" fill="${fill}" aria-hidden="true"><path d="M7 0l1.9 4.2 4.6.5-3.4 3.1.9 4.5L7 10.2l-4 2.1.9-4.5L.5 4.7l4.6-.5z"/></svg>`;
  return star('#84BE43').repeat(rating) + star('#D9D9D9').repeat(5 - rating);
}

function buildCard(item, index) {
  const starsLabel = item.querySelector('.jdgm-carousel-item__review-rating')?.getAttribute('aria-label') || '5 stars';
  const img = item.querySelector('.jdgm-carousel-item__product-image')?.getAttribute('data-src') || '';
  const url = item.querySelector('.jdgm-carousel-item__product')?.getAttribute('href') || '#';
  const productName = item.querySelector('.jdgm-carousel-item__product-title')?.textContent.trim() || '';
  const reviewTitle = item.querySelector('.jdgm-carousel-item__review-title')?.textContent.trim() || '';
  const reviewCopy = item.querySelector('.jdgm-carousel-item__review-body p')?.textContent.trim() || '';
  const authorName = item.querySelector('.jdgm-carousel-item__reviewer-name')?.textContent.trim() || '';

  const title = truncate(reviewTitle, 28);
  const copy = truncate(reviewCopy, 160);

  const card = document.createElement('div');
  card.className = 'vc-review-card';
  card.innerHTML = `
    <a class="vc-review-card__product" href="${url}">
      <img src="${img}" loading="lazy" width="110" height="110" alt="${productName}">
      <span>${productName}</span>
    </a>
    <div class="vc-review-card__stars" role="img" aria-label="${starsLabel}">
      ${starsMarkup(starsLabel)}
    </div>
    <svg class="vc-review-card__quote" width="26" height="27" viewBox="0 0 26 27" fill="none" aria-hidden="true">
      <path d="M5.77265 14.7729L5.04331 14.7553C-2.94541 14.6144 -0.282508 2.42584 8.26594 5.64912C14.5755 8.02695 13.4052 18.965 6.65462 20.7616C3.66945 21.5542 0.345061 20.9905 2.82139 20.1099C4.94154 19.3701 6.65462 17.2388 6.5359 15.4951C6.50197 15.1076 6.17971 14.7729 5.77265 14.7729Z" fill="#CCCCCC"/>
      <path d="M21.3602 5.20875C29.0436 8.2735 25.9227 21.6598 17.3912 22.2411C15.1693 22.3996 14.5927 21.8536 16.2718 21.2019C17.9001 20.5678 19.9524 17.5735 20.1729 15.5479C20.2238 15.1428 19.9185 14.8081 19.5284 14.8081C17.2895 14.8081 16.3566 14.8081 15.0506 12.9411C11.8449 8.32635 16.1022 3.11274 21.3602 5.20875Z" fill="#CCCCCC"/>
    </svg>
    <strong class="vc-review-card__title">${title.text}</strong>
    <p class="vc-review-card__copy">${copy.text}</p>
    ${copy.truncated ? '<button type="button" class="vc-review-card__see-more">See more</button>' : ''}
    <p class="vc-review-card__author">
      <svg width="13" height="12" viewBox="0 0 13 12" fill="none" aria-hidden="true"><path d="M11.592 5.99985C11.592 8.98219 9.17433 11.3999 6.19199 11.3999C3.20965 11.3999 0.791992 8.98219 0.791992 5.99985C0.791992 3.01752 3.20965 0.599854 6.19199 0.599854C7.03922 0.599854 7.84088 0.794967 8.55449 1.14271M10.5795 2.62485L5.85449 7.34985L4.50449 5.99985" stroke="#84BE43" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      <span>${authorName}</span>
    </p>
  `;

  card.dataset.fullTitle = reviewTitle;
  card.dataset.fullCopy = reviewCopy;
  card.dataset.productImage = img;
  card.dataset.productName = productName;
  card.dataset.productUrl = url;
  card.dataset.authorName = authorName;

  return card;
}

function initModal(root) {
  const modal = document.getElementById('vc-review-modal');
  if (!modal) return;

  const closeModal = () => {
    modal.hidden = true;
  };

  root.addEventListener('click', (event) => {
    const trigger = event.target.closest('.vc-review-card__see-more');
    if (!trigger) return;
    const card = trigger.closest('.vc-review-card');
    if (!card) return;

    modal.querySelector('[data-vc-modal-image]').src = card.dataset.productImage;
    modal.querySelector('[data-vc-modal-product]').textContent = card.dataset.productName;
    modal.querySelector('[data-vc-modal-title]').textContent = card.dataset.fullTitle;
    modal.querySelector('[data-vc-modal-copy]').textContent = card.dataset.fullCopy;
    modal.querySelector('[data-vc-modal-author]').textContent = card.dataset.authorName;
    modal.querySelector('[data-vc-modal-link]').href = card.dataset.productUrl;
    modal.hidden = false;
  });

  modal.querySelector('.vc-review-modal__close')?.addEventListener('click', closeModal);
  modal.addEventListener('click', (event) => {
    if (event.target === modal) closeModal();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.hidden) closeModal();
  });
}

function initDots(track, dotsContainer, onDotClick) {
  const cards = [...track.querySelectorAll('.vc-review-card')];
  if (cards.length === 0) return;

  const desktopViewport = window.matchMedia('(min-width: 1024px)');
  let desktopTargets = [];
  const dots = [];
  const setActive = (index) => {
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  };

  // Suppresses IntersectionObserver updates while a dot-click scroll is in-flight.
  // Without this, the observer fires for every intermediate card the smooth scroll
  // passes through, making the dots visibly count down/up to the target (most noticeable
  // for dots 1 and 2 when scrolling back from the end of the carousel).
  let isDotScrolling = false;
  let dotScrollTimer = null;

  cards.forEach((card, index) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'vc-reviews-carousel__dot';
    dot.setAttribute('role', 'tab');
    dot.setAttribute('aria-label', `Go to review ${index + 1}`);
    dot.addEventListener('click', () => {
      // Measure before onDotClick/stop() since stop() fires scrollTo-instant which
      // doesn't move position but cancels in-flight animation -- rects stay valid.
      const cardRect = card.getBoundingClientRect();
      const trackRect = track.getBoundingClientRect();
      // Subtracting scroll-padding-left matches where CSS scroll-snap would naturally settle this
      // card (see the tablet/mobile peek CSS) -- without it, this explicit rect math always lands
      // the card flush against the track's true edge regardless of scroll-padding, showing no left
      // peek on a dot-jump even though a drag/swipe to the same card would show one. Clamped to 0
      // since the first card has nothing to peek before it and would otherwise compute negative.
      const scrollPaddingLeft = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
      let targetScrollLeft = Math.max(0, track.scrollLeft + (cardRect.left - trackRect.left) - scrollPaddingLeft);
      // The generic start-aligned formula above leaves the actual last card only partially
      // peeking in from the right (it aligns it the same "start + scroll-padding" way as every
      // OTHER card, which is correct for interior cards but means the final card, having nothing
      // after it, never gets pulled fully into view) -- clamping up to the real scrollable max for
      // the last card specifically ensures its own dot actually shows it in full, not just a sliver.
      if (index === cards.length - 1) {
        targetScrollLeft = Math.max(targetScrollLeft, track.scrollWidth - track.clientWidth);
      }
      if (desktopViewport.matches) targetScrollLeft = desktopTargets[index] ?? targetScrollLeft;

      // Update active state immediately -- no waiting for the IntersectionObserver.
      setActive(index);

      // Block observer updates for the duration of the smooth scroll. 800ms covers
      // the longest browser smooth-scroll animation (typically 300-700ms).
      isDotScrolling = true;
      clearTimeout(dotScrollTimer);
      dotScrollTimer = setTimeout(() => {
        isDotScrolling = false;
        if (desktopViewport.matches) updateDesktopActive();
      }, 800);

      if (onDotClick) onDotClick();
      track.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    });
    dotsContainer.appendChild(dot);
    dots.push(dot);
  });

  setActive(0);

  // Each callback only reports cards whose ratio CHANGED since the last firing, not every
  // observed card -- picking "best" from just that partial batch let a stale, no-longer-visible
  // card win whenever it happened to be the only one in a given batch, causing the active dot to
  // jump to arbitrary indices during drag/scroll. Track every card's last-known ratio and always
  // pick the true global best.
  const ratios = new Map(cards.map((card) => [card, 0]));
  const updateDesktopActive = () => {
    let nearestIndex = 0;
    let nearestDistance = Infinity;
    dots.forEach((dot, index) => {
      if (dot.hidden) return;
      const distance = Math.abs(track.scrollLeft - desktopTargets[index]);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });
    setActive(nearestIndex);
  };

  const refreshDots = () => {
    if (!desktopViewport.matches) {
      dots.forEach((dot) => { dot.hidden = false; });
      desktopTargets = [];
      return;
    }

    // Several final cards share the same maximum scroll position when multiple cards fit.
    // Hide their duplicate dots so each visible dot has a position the trackpad can reach.
    const firstLeft = cards[0].getBoundingClientRect().left;
    const scrollPaddingLeft = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    let previousTarget = -Infinity;
    desktopTargets = cards.map((card) =>
      Math.min(maxScrollLeft, Math.max(0, card.getBoundingClientRect().left - firstLeft - scrollPaddingLeft))
    );
    dots.forEach((dot, index) => {
      dot.hidden = index > 0 && desktopTargets[index] <= previousTarget + 1;
      if (!dot.hidden) previousTarget = desktopTargets[index];
    });
    updateDesktopActive();
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0));

      // Don't overwrite the immediately-set active dot while a dot-click scroll is
      // animating -- the observer would otherwise cycle through every intermediate card.
      if (isDotScrolling) return;
      if (desktopViewport.matches) {
        updateDesktopActive();
        return;
      }

      let bestCard = null;
      let bestRatio = -1;
      cards.forEach((card) => {
        const ratio = ratios.get(card);
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestCard = card;
        }
      });
      if (bestCard) setActive(cards.indexOf(bestCard));
    },
    { root: track, threshold: [0, 0.25, 0.5, 0.75, 1] }
  );
  cards.forEach((card) => observer.observe(card));
  track.addEventListener('scroll', () => {
    if (desktopViewport.matches && !isDotScrolling) updateDesktopActive();
  }, { passive: true });
  const resizeObserver = new ResizeObserver(() => {
    if (track.isConnected) refreshDots();
    else resizeObserver.disconnect();
  });
  resizeObserver.observe(track);
  refreshDots();
}

/**
 * Adds drag-to-scroll for mouse and pen without Flickity. Narrow mobile viewports use native
 * touch scrolling instead; their CSS allows horizontal panning and browser momentum.
 */
function initDrag(track) {
  let isPointerDown = false;
  let isDragging = false;
  let startX = 0;
  let startScrollLeft = 0;
  const mobileViewport = window.matchMedia('(max-width: 600px)');

  const endDrag = (event) => {
    if (!isPointerDown) return;
    isPointerDown = false;
    if (isDragging) {
      isDragging = false;
      track.style.scrollSnapType = '';
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      // A drag ending over a link/button shouldn't also fire its click (matches Flickity's own
      // dragThreshold-suppresses-click behavior). Suppress just the next click, synchronously.
      track.addEventListener('click', (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
      }, { capture: true, once: true });
    }
  };

  track.addEventListener('pointerdown', (event) => {
    // Let the browser handle touch momentum and scroll snapping on narrow screens.
    // Keep the existing custom drag for mouse, pen, and wider viewports.
    if (mobileViewport.matches && event.pointerType === 'touch') return;
    isPointerDown = true;
    isDragging = false;
    startX = event.clientX;
    startScrollLeft = track.scrollLeft;
  });

  track.addEventListener('pointermove', (event) => {
    if (!isPointerDown) return;
    const delta = event.clientX - startX;
    if (!isDragging && Math.abs(delta) > 5) {
      isDragging = true;
      track.style.scrollSnapType = 'none';
      track.classList.add('is-dragging');
      track.setPointerCapture(event.pointerId);
    }
    if (isDragging) {
      track.scrollLeft = startScrollLeft - delta;
      event.preventDefault();
    }
  });

  track.addEventListener('pointerup', endDrag);
  track.addEventListener('pointercancel', endDrag);
}

function initAutoScroll(track) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;

  let timer = null;
  let isInViewport = false;
  let isPaused = false;
  let isTouchPaused = false;
  let wasManuallyUsed = false;
  let touchResumeTimer = null;
  const mobileViewport = window.matchMedia('(max-width: 600px)');
  const intervalMs = 3000;
  const touchPauseMs = 8000;

  const step = () => {
    const card = track.querySelector('.vc-review-card');
    if (!card) return;
    const cardWidth = card.getBoundingClientRect().width + 24;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    track.scrollTo({ left: atEnd ? 0 : track.scrollLeft + cardWidth, behavior: 'smooth' });
  };

  const stop = () => {
    if (timer) {
      window.clearInterval(timer);
      timer = null;
    }
    // clearInterval only prevents the NEXT tick -- a smooth-scroll animation already dispatched
    // by the last tick keeps animating regardless, and fights with the user's own drag/scroll
    // once they start interacting (looks like the carousel randomly jumping/skipping cards).
    // Snapping to the current position cancels any in-flight animation immediately.
    track.scrollTo({ left: track.scrollLeft, behavior: 'instant' });
  };
  const start = () => {
    stop();
    timer = window.setInterval(step, intervalMs);
  };
  // Only run while in viewport, not hovered/focused, and tab visible -- otherwise this fires
  // every 3s from page load even while offscreen.
  const sync = () => {
    if (isInViewport && !isPaused && !isTouchPaused && !wasManuallyUsed && !document.hidden) start();
    else stop();
  };

  const stopForDesktopInteraction = () => {
    if (mobileViewport.matches || wasManuallyUsed) return;
    wasManuallyUsed = true;
    sync();
  };

  const pauseForTouch = () => {
    isTouchPaused = true;
    clearTimeout(touchResumeTimer);
    sync();
  };

  const resumeAfterTouch = () => {
    clearTimeout(touchResumeTimer);
    touchResumeTimer = setTimeout(() => {
      isTouchPaused = false;
      if (track.isConnected) sync();
    }, touchPauseMs);
  };

  // Keep a selected desktop review in place. Mobile resumes after a reading pause.
  const pauseForDotClick = () => {
    if (mobileViewport.matches) {
      pauseForTouch();
      resumeAfterTouch();
      return;
    }
    stopForDesktopInteraction();
  };

  track.addEventListener('pointerenter', () => {
    isPaused = true;
    sync();
  });
  track.addEventListener('pointerdown', (event) => {
    if (mobileViewport.matches && event.pointerType === 'touch') pauseForTouch();
    else stopForDesktopInteraction();
  });
  track.addEventListener('wheel', stopForDesktopInteraction, { passive: true });
  track.addEventListener('pointerup', (event) => {
    if (mobileViewport.matches && event.pointerType === 'touch') resumeAfterTouch();
  });
  track.addEventListener('pointercancel', (event) => {
    if (mobileViewport.matches && event.pointerType === 'touch') resumeAfterTouch();
  });
  track.addEventListener('pointerleave', () => {
    isPaused = false;
    sync();
  });
  track.addEventListener('focusin', () => {
    isPaused = true;
    sync();
  });
  track.addEventListener('focusout', () => {
    isPaused = false;
    sync();
  });
  document.addEventListener('visibilitychange', sync);

  const observer = new IntersectionObserver(
    (entries) => {
      isInViewport = entries[0].isIntersecting;
      sync();
    },
    { threshold: 0.1 }
  );
  observer.observe(track);

  return { pauseForDotClick };
}

/**
 * Reads whatever review items are currently sitting in the hidden Judge.me source and, if any
 * are present, builds cards + wires up the carousel. Returns true/false so callers (the eager
 * path below, and the lazy-load retry loop) know whether to keep waiting.
 */
function populateFromSource(section) {
  const source = section.querySelector('.vc-happy-growers__judgeme-source');
  const track = section.querySelector('[data-vc-reviews-track]');
  const dotsContainer = section.querySelector('[data-vc-reviews-dots]');
  if (!source || !track) return false;

  const items = source.querySelectorAll('.jdgm-widget.jdgm-carousel .jdgm-carousel-item');
  if (items.length === 0) return false;

  track.querySelector('.vc-reviews-carousel__skeleton')?.remove();

  items.forEach((item, index) => {
    track.appendChild(buildCard(item, index));
  });

  initModal(track.closest('.vc-reviews-carousel'));
  initDrag(track);
  const autoScroll = initAutoScroll(track);
  if (dotsContainer) initDots(track, dotsContainer, autoScroll?.pauseForDotClick);

  // source is a one-time data source (see file header) -- once cards are built above it's ~300
  // dead DOM nodes. Delayed removal avoids racing Judge.me's own async widget population/scripts,
  // which can still be settling well after insertion (see the dot-desync entry in project notes).
  setTimeout(() => source.remove(), 8000);
  return true;
}

/**
 * innerHTML/DOMParser-inserted <script> tags never execute (a deliberate, universal browser
 * behavior regardless of insertion method) -- Judge.me's widget bundle is loaded via literal
 * <script src> tags in its app-block markup, so those have to be individually recreated via
 * createElement to actually run once the fetched HTML lands in the live document.
 */
function executeScripts(container) {
  container.querySelectorAll('script').forEach((oldScript) => {
    const newScript = document.createElement('script');
    for (const attr of oldScript.attributes) newScript.setAttribute(attr.name, attr.value);
    newScript.textContent = oldScript.textContent;
    oldScript.replaceWith(newScript);
  });
}

/**
 * Judge.me's own widget script populates .jdgm-carousel-item asynchronously (real API call under
 * the hood) -- there's no single "now it's ready" event, so this polls via MutationObserver
 * instead of assuming any fixed delay. Falls back to giving up after timeoutMs so a genuine
 * Judge.me failure doesn't leave the skeleton spinning forever.
 */
function waitForItems(section, timeoutMs = 12000) {
  if (populateFromSource(section)) return;

  const source = section.querySelector('.vc-happy-growers__judgeme-source');
  if (!source) return;

  const observer = new MutationObserver(() => {
    if (populateFromSource(section)) {
      observer.disconnect();
      clearTimeout(timer);
    }
  });
  observer.observe(source, { childList: true, subtree: true });

  const timer = setTimeout(() => observer.disconnect(), timeoutMs);
}

/**
 * Moves the hidden Judge.me app-block markup out of its inert <template> and into the live
 * document -- see sections/vc-happy-growers.liquid (the judgeme_lazy_homepage <template> wrapper)
 * for why this is a <template> in the first place. Only <link>/<img> children start fetching
 * automatically once live; <script> children still need the createElement recreation in
 * executeScripts (browsers never auto-run a script that arrives via DOM insertion, template or not).
 */
function loadJudgemeSource(section) {
  const source = section.querySelector('.vc-happy-growers__judgeme-source');
  const template = source?.querySelector('template[data-vc-judgeme-template]');
  if (!source || !template) return;

  source.appendChild(template.content.cloneNode(true));
  template.remove();
  executeScripts(source);
  waitForItems(section);
}

/**
 * Two independent triggers, whichever fires first: the carousel nearing the viewport, or the
 * visit having been idle a couple seconds regardless of scroll position. The idle trigger exists
 * specifically to beat fast flick-scrolling -- most visitors who scroll quickly have already been
 * on the page a moment first, so by the time they flick down the fetch is usually already
 * underway. A genuine instant-bounce visitor (gone before either fires) never triggers either,
 * preserving the full byte savings for them.
 */
function initLazyJudgeme(section) {
  const track = section.querySelector('[data-vc-reviews-track]');
  if (!track) return;

  let triggered = false;
  const trigger = () => {
    if (triggered) return;
    triggered = true;
    observer.disconnect();
    clearTimeout(idleTimer);
    loadJudgemeSource(section);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) trigger();
    },
    { rootMargin: '900px 0px' }
  );
  observer.observe(track);

  const idleTimer = setTimeout(trigger, 2500);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.vc-happy-growers').forEach((section) => {
    const source = section.querySelector('.vc-happy-growers__judgeme-source');
    if (source?.querySelector('template[data-vc-judgeme-template]')) {
      initLazyJudgeme(section);
      return;
    }

    // Unchanged eager path -- About/Care/FAQ/Contact pages still render the Judge.me app block
    // inline, so it's normally already present by DOMContentLoaded.
    populateFromSource(section);
  });
});
